import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Scissors, Play, Upload, Trash2, X, CheckCircle2, Loader, Send, Users, Star } from "lucide-react";
import ClipPlayer from "@/components/ClipPlayer";
import ShareClipModal from "@/components/ShareClipModal";
import axios from "axios";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_DURATION_SEC = 30;
const MAX_FILE_BYTES = 100 * 1024 * 1024;

const ClipsPage = () => {
  const { api, user } = useAuth();
  const [tab, setTab] = useState("my");
  const [myClips, setMyClips] = useState([]);
  const [sharedClips, setSharedClips] = useState([]);
  const [activeClip, setActiveClip] = useState(null);
  const [shareClip, setShareClip] = useState(null);
  const [uploadState, setUploadState] = useState(null); // null | {file, title, duration, progress, error, done}
  const fileInputRef = useRef(null);

  const [favoriteClips, setFavoriteClips] = useState([]);
  const [favPending, setFavPending] = useState({});

  const reloadClips = () => {
    api().get("/clips/my").then(r => setMyClips(r.data)).catch(() => {});
    api().get("/clips/shared").then(r => setSharedClips(r.data)).catch(() => {});
    api().get("/clips/favorites").then(r => setFavoriteClips(r.data)).catch(() => {});
  };

  useEffect(() => { reloadClips(); /* eslint-disable-next-line */ }, [api]);

  const favoriteIds = new Set(favoriteClips.map(c => String(c.id)));
  const clips = tab === "my" ? myClips : tab === "shared" ? sharedClips : favoriteClips;

  const toggleFavorite = async (clip) => {
    const id = String(clip.id);
    if (favPending[id]) return;
    const wasFav = favoriteIds.has(id);
    setFavPending(p => ({ ...p, [id]: true }));
    // Optimistic update; rolled back if the server refuses.
    setFavoriteClips(prev => wasFav ? prev.filter(c => String(c.id) !== id) : [clip, ...prev]);
    try {
      if (wasFav) await api().delete(`/clips/${clip.id}/favorite`);
      else await api().put(`/clips/${clip.id}/favorite`);
    } catch (e) {
      setFavoriteClips(prev => wasFav
        ? (prev.some(c => String(c.id) === id) ? prev : [clip, ...prev])
        : prev.filter(c => String(c.id) !== id));
      alert(e.response?.data?.detail || "Could not update favorite");
    } finally {
      setFavPending(p => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  const deleteClip = async (id) => {
    await api().delete(`/clips/${id}`);
    setMyClips(prev => prev.filter(c => c.id !== id));
    setFavoriteClips(prev => prev.filter(c => c.id !== id));
  };

  const saveSharedClipToMine = async (clip) => {
    // Copies a clip that was shared with me into my own clips library.
    // Idempotent — if I already saved this source, the backend returns the
    // existing copy so the UI just moves it into "My Clips" without dup-ing.
    try {
      const r = await api().post(`/clips/${clip.id}/save`);
      const newClip = r.data;
      setMyClips(prev => prev.some(c => c.id === newClip.id) ? prev : [newClip, ...prev]);
      // Tag the shared clip locally so the button flips to "Saved ✓"
      setSharedClips(prev => prev.map(c => c.id === clip.id ? { ...c, _saved_to_mine: true } : c));
      setFavoriteClips(prev => prev.map(c => c.id === clip.id ? { ...c, _saved_to_mine: true } : c));
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save clip");
    }
  };

  // ─── Clip file upload flow ───
  const readDuration = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration); };
    v.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read video")); };
    v.src = url;
  });

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    if (!["video/mp4", "video/quicktime"].includes(file.type)) {
      setUploadState({ file, error: "Only MP4 or MOV video files are allowed" });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setUploadState({ file, error: "File too large (max 100 MB)" });
      return;
    }
    let duration = 0;
    try { duration = await readDuration(file); }
    catch { setUploadState({ file, error: "Could not read video metadata" }); return; }
    if (duration > MAX_DURATION_SEC + 0.5) {
      setUploadState({ file, duration, error: `Clip is ${duration.toFixed(1)}s — must be ${MAX_DURATION_SEC}s or shorter.` });
      return;
    }
    setUploadState({ file, duration, title: file.name.replace(/\.[^/.]+$/, ""), description: "", progress: 0 });
  };

  const confirmUpload = async () => {
    if (!uploadState || uploadState.error) return;
    const fd = new FormData();
    fd.append("file", uploadState.file);
    fd.append("title", uploadState.title || "My Clip");
    fd.append("description", uploadState.description || "");
    fd.append("duration_seconds", String(uploadState.duration || 0));
    try {
      setUploadState(s => ({ ...s, progress: 1 }));
      const token = localStorage.getItem("ysv_token");
      await axios.post(`${API_ROOT}/clips/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setUploadState(s => ({ ...s, progress: Math.round((evt.loaded / evt.total) * 100) }));
        },
      });
      setUploadState(s => ({ ...s, done: true, progress: 100 }));
      reloadClips();
      setTimeout(() => setUploadState(null), 1200);
    } catch (err) {
      setUploadState(s => ({ ...s, error: err.response?.data?.detail || "Upload failed" }));
    }
  };

  if (activeClip) {
    return (
      <ClipPlayer
        clips={[activeClip]}
        title={activeClip.title}
        subtitle={activeClip.game_title}
        onBack={() => setActiveClip(null)}
      />
    );
  }

  return (
    <div data-testid="clips-page" style={{ padding: '32px 40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 700 }}>Clips</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime"
            onChange={onFilePicked}
            style={{ display: 'none' }}
            data-testid="import-clip-file"
          />
          <button
            data-testid="import-clips-btn"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            style={{
              background: 'var(--cyan)', color: '#000', border: 'none',
              padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif",
            }}
          ><Upload size={14} /> Import</button>
          <Link to="/games" data-testid="new-clip-btn" style={{
            background: '#ff6b00', color: '#fff', textDecoration: 'none',
            padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 6,
          }}>+ New</Link>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid var(--dark-border)' }}>
        {[
          { key: "my", label: `My Clips (${myClips.length})` },
          { key: "shared", label: `Shared (${sharedClips.length})` },
          { key: "favorites", label: `Favorites (${favoriteClips.length})` },
        ].map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} style={{
            background: 'none', border: 'none', padding: '12px 24px',
            color: tab === t.key ? 'var(--cyan)' : 'var(--text-secondary)',
            fontSize: 14, fontWeight: tab === t.key ? 600 : 400,
            borderBottom: tab === t.key ? '2px solid var(--cyan)' : '2px solid transparent',
            cursor: 'pointer', marginBottom: -2,
            fontFamily: "'DM Sans', sans-serif",
          }}>{t.label}</button>
        ))}
      </div>

      {/* Clips List */}
      {clips.length === 0 && tab === "favorites" ? (
        <div data-testid="favorites-empty" style={{ textAlign: 'center', padding: 80 }}>
          <Star size={48} style={{ color: 'var(--text-secondary)', opacity: 0.3, marginBottom: 16 }} />
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No Favorites Yet</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Tap the star on any clip to find it here later</p>
        </div>
      ) : clips.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Scissors size={48} style={{ color: 'var(--text-secondary)', opacity: 0.3, marginBottom: 16 }} />
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>No Clips Created</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 20 }}>Watch a game and create your first clip</p>
          <Link to="/games" data-testid="browse-games-btn" className="btn-primary" style={{ textDecoration: 'none', padding: '10px 24px', fontSize: 14 }}>Browse Games</Link>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {clips.map(clip => {
            const isFav = favoriteIds.has(String(clip.id));
            // On the Favorites tab, show the actions that match where the clip came from.
            const mode = tab === "favorites"
              ? (String(clip.created_by) === String(user?.id) ? "my" : "shared")
              : tab;
            return (
            <div key={clip.id} data-testid={`clip-card-${clip.id}`} className="glass-card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
              <button
                type="button"
                data-testid={`favorite-clip-${clip.id}`}
                aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
                aria-pressed={isFav}
                title={isFav ? "Remove from favorites" : "Add to favorites"}
                onClick={() => toggleFavorite(clip)}
                disabled={!!favPending[String(clip.id)]}
                style={{
                  position: 'absolute', top: 8, right: 8, zIndex: 1,
                  background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: 999,
                  width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', padding: 0,
                }}
              >
                <Star size={16} style={{ color: isFav ? '#ffc107' : 'var(--text-secondary)' }} fill={isFav ? '#ffc107' : 'none'} />
              </button>
              <button
                type="button"
                data-testid={`play-clip-${clip.id}`}
                onClick={() => setActiveClip(clip)}
                style={{
                  background: 'rgba(0,229,255,0.05)', padding: '24px 20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', cursor: 'pointer', width: '100%',
                }}
              >
                <Play size={32} style={{ color: 'var(--cyan)' }} />
              </button>
              <div style={{ padding: '16px 20px' }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 4, cursor: 'pointer' }} onClick={() => setActiveClip(clip)}>{clip.title}</h3>
                <p style={{ fontSize: 12, color: 'var(--cyan)', marginBottom: 4 }}>{clip.game_title}</p>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>{(Math.max(0, (Number(clip.end_time) || 0) - (Number(clip.start_time) || 0))).toFixed(1)}s &bull; by {clip.created_by_name}</p>
                {clip.tags?.length > 0 && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
                    {clip.tags.map(t => <span key={t} style={{ background: 'rgba(0,229,255,0.1)', color: 'var(--cyan)', fontSize: 10, padding: '2px 8px', borderRadius: 10 }}>{t}</span>)}
                  </div>
                )}
                {mode === "my" && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 4 }}>
                    <button onClick={() => deleteClip(clip.id)} data-testid={`delete-clip-${clip.id}`} style={{ background: 'none', border: 'none', color: '#f44336', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Trash2 size={12} /> Delete
                    </button>
                    <button
                      onClick={() => setShareClip(clip)}
                      data-testid={`share-clip-${clip.id}`}
                      style={{
                        background: 'none', border: 'none', color: 'var(--cyan)', cursor: 'pointer',
                        fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto',
                      }}>
                      <Send size={12} /> Share
                      {(clip.shared_with || []).length > 0 && (
                        <span style={{ background: 'rgba(0,229,255,0.15)', color: 'var(--cyan)', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>
                          {(clip.shared_with || []).length}
                        </span>
                      )}
                    </button>
                  </div>
                )}
                {mode === "shared" && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 4 }}>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Users size={11} /> from {clip.created_by_name || 'a teammate'}
                    </span>
                    <button
                      onClick={() => saveSharedClipToMine(clip)}
                      data-testid={`save-shared-clip-${clip.id}`}
                      disabled={clip._saved_to_mine}
                      style={{
                        marginLeft: 'auto', padding: '4px 12px',
                        background: clip._saved_to_mine ? 'rgba(76,175,80,0.15)' : 'var(--cyan)',
                        color: clip._saved_to_mine ? '#4caf50' : '#000',
                        border: 'none', borderRadius: 8, fontSize: 11, fontWeight: 700,
                        cursor: clip._saved_to_mine ? 'default' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                      {clip._saved_to_mine ? <><CheckCircle2 size={12} /> Saved</> : <>Save to My Clips</>}
                    </button>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {shareClip && (
        <ShareClipModal
          clip={shareClip}
          onClose={() => setShareClip(null)}
          onShared={(updated) => {
            if (updated) {
              setMyClips((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
              setFavoriteClips((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            }
          }}
        />
      )}

      {uploadState && (
        <div data-testid="clip-upload-modal" onClick={(e) => { if (e.target === e.currentTarget && !uploadState.progress) setUploadState(null); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: 16,
        }}>
          <div className="glass-card" style={{ padding: 24, width: 'min(96vw, 460px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Outfit', sans-serif", display: 'flex', alignItems: 'center', gap: 8 }}>
                <Upload size={18} style={{ color: 'var(--cyan)' }} /> Upload a Clip
              </h3>
              {!uploadState.progress && (
                <button data-testid="clip-upload-close" onClick={() => setUploadState(null)} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
                  <X size={20} />
                </button>
              )}
            </div>

            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
              File: <strong style={{ color: '#fff' }}>{uploadState.file.name}</strong>{uploadState.duration ? <> · {uploadState.duration.toFixed(1)}s · {(uploadState.file.size / 1024 / 1024).toFixed(1)} MB</> : null}
            </p>

            {uploadState.error ? (
              <div data-testid="clip-upload-error" style={{ padding: 12, background: 'rgba(244,67,54,0.12)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 10, color: '#f44336', fontSize: 13, marginBottom: 12 }}>
                {uploadState.error}
              </div>
            ) : uploadState.done ? (
              <div data-testid="clip-upload-success" style={{ padding: 16, textAlign: 'center', color: '#4caf50' }}>
                <CheckCircle2 size={36} style={{ marginBottom: 8 }} />
                <p style={{ fontSize: 14, fontWeight: 700 }}>Clip uploaded!</p>
              </div>
            ) : uploadState.progress ? (
              <div style={{ padding: '12px 0' }}>
                <div style={{ height: 8, background: '#1a1a1f', borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                  <div data-testid="clip-upload-progress" style={{ height: '100%', width: `${uploadState.progress}%`, background: 'var(--cyan)', transition: 'width 0.2s' }} />
                </div>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> Uploading… {uploadState.progress}%
                </p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Title
                  <input data-testid="clip-upload-title" className="input-field" value={uploadState.title || ""} onChange={e => setUploadState(s => ({ ...s, title: e.target.value }))} />
                </label>
                <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 12 }}>Description <span style={{ opacity: 0.6 }}>(optional)</span>
                  <textarea data-testid="clip-upload-desc" className="input-field" rows={2} value={uploadState.description || ""} onChange={e => setUploadState(s => ({ ...s, description: e.target.value }))} />
                </label>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 14 }}>
                  Clips must be <strong>30 seconds or shorter</strong>. Uploaded clips appear in "My Clips".
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setUploadState(null)} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button data-testid="clip-upload-submit" onClick={confirmUpload} className="btn-primary" style={{ flex: 1 }}>Upload clip</button>
                </div>
              </>
            )}

            {uploadState.error && (
              <button onClick={() => setUploadState(null)} style={{ width: '100%', padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Close</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClipsPage;
